import type { LiveParticipant } from './types'
import styles from './live.module.css'

export interface CastWallProps {
  participants: readonly LiveParticipant[]
  /**
   * Whether this viewer is the Game Master.
   *
   * It changes what the wall *contains*, not how it is styled: only the Game Master is ever sent
   * an unknown participant, and only their wall has anyone on it to draw faded.
   */
  canControl: boolean
  selectedId: string | null
  onSelect: (participantId: string) => void
  /** Said when there is nobody to show, which differs by phase and by who is reading. */
  emptyText: string
}

/**
 * The faces, where the map goes.
 *
 * **This is the pre-encounter screen**, and it is a wall of portraits rather than a list of names
 * because the thing being established before an encounter starts is *who is here* — and a face
 * does that in a glance where a name has to be read. It is the one place in the app that shows a
 * portrait at its full size rather than as a 44px circle, which is why participants carry two
 * portrait URLs.
 *
 * Each item is one *instance*: a Temp character spawned six times is six portraits of the same
 * face, told apart by the number drawn over each. That is what the number is for — a wall is read
 * at a glance, and six identical faces read as one until they are counted.
 *
 * A player sees exactly the people the table has met. Not "sees them greyed out", not "sees a
 * placeholder": the server does not send an unknown participant to a player at all, so there is
 * nothing here to leak. The Game Master's own wall carries everyone, with the ones nobody has met
 * yet drawn faded and labelled — which is the same treatment a hidden token gets on the map, and
 * for the same reason.
 */
export function CastWall({
  participants,
  canControl,
  selectedId,
  onSelect,
  emptyText,
}: CastWallProps) {
  if (participants.length === 0) {
    return (
      <div className={styles.mapEmpty}>
        <p>{emptyText}</p>
      </div>
    )
  }

  // Sides together, then by name — the same order the legend uses, because a Game Master reading
  // either of them is looking for a side. Numeric collation for the same reason it does: a horde's
  // names end in numbers, and Zombie 10 belongs after Zombie 9 rather than after Zombie 1.
  const ordered = [...participants].sort(
    (a, b) =>
      (a.side ?? '').localeCompare(b.side ?? '') ||
      a.name.localeCompare(b.name, undefined, { numeric: true }),
  )

  return (
    <ul className={styles.wall} aria-label="Who is here">
      {ordered.map((participant) => (
        <li key={participant.participantId}>
          <button
            type="button"
            className={[
              styles.wallItem,
              selectedId === participant.participantId ? styles.wallSelected : '',
              participant.isKnown ? '' : styles.wallUnknown,
              participant.isDead ? styles.wallDead : '',
            ].join(' ')}
            aria-pressed={selectedId === participant.participantId}
            aria-label={`${participant.name}${participant.side === null ? '' : `, ${participant.side}`}${
              participant.isDead ? ', out of the encounter' : ''
            }${canControl && !participant.isKnown ? ', not introduced yet' : ''}`}
            onClick={() => onSelect(participant.participantId)}
          >
            <span className={styles.wallFrame}>
              {participant.fullPortraitUrl === null ? (
                <span className={styles.wallInitial} aria-hidden="true">
                  {participant.name.slice(0, 1).toUpperCase()}
                </span>
              ) : (
                <img
                  className={styles.wallPortrait}
                  src={participant.fullPortraitUrl}
                  alt=""
                  loading="lazy"
                  draggable={false}
                />
              )}

              {/* The number, over the top corner.
                  Six clones of one template are six copies of one portrait, so on a wall of
                  faces the number is the only thing telling them apart at a glance — and the
                  caption underneath, which says "Zombie 2" in full, is the thing you have to
                  stop and read. `aria-hidden`, since the button's label carries the name.
                  Top-left because the veil below owns the bottom of the frame. */}
              {participant.ordinal !== null && (
                <span className={styles.ordinalWall} aria-hidden="true">
                  {participant.ordinal}
                </span>
              )}

              {/* Said over the face, not only under it.
                  This wall is the Game Master's whole view of the room in the half of an evening
                  where the question is "who have they met yet" — and a greyed portrait is easy to
                  read as "this one is dead" or "this image failed to load". The word is what makes
                  it unambiguous. Only the Game Master is ever sent somebody the table has not met,
                  so this only ever renders on their screen; `aria-hidden` because the button's own
                  label already says it, and reading it twice is noise. */}
              {!participant.isKnown && (
                <span className={styles.wallVeil} aria-hidden="true">
                  <span className={styles.wallVeilText}>Not introduced</span>
                </span>
              )}
            </span>

            <span className={styles.wallCaption}>
              <span className={styles.wallName}>{participant.name}</span>
              {/* Not "not introduced" — the veil above says that, in larger type, twenty pixels
                  higher. Saying it twice on a card this small is noise, and the one that goes is
                  the quieter one. */}
              <span className={styles.rowMeta}>
                {[participant.side, participant.isDead ? 'out' : null]
                  .filter((part) => part !== null && part !== '')
                  .join(' · ')}
              </span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}
